import './LineChart.scss';
import * as R from 'ramda';
import { ComponentProps, useCallback, useMemo, useState, FC } from 'react';
import { MdAutoGraph, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { CartesianGrid, Line, LineChart as LC, Tooltip, XAxis, YAxis } from 'recharts';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { customColorMapping, Neutral200, Neutral500 } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '../ChartConstants';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import { headerFormatter, useDynamicYAxisWidth } from '../ChartUtils';
import { renderXAxisLabel, X_AXIS_LABEL_HEIGHT } from '../XAxisLabel';

const GRAY_COLOR = Neutral500;

interface CustomColorMapping {
  name: string;
  color: string;
}
interface LineDataItem {
  dataKey: string;
  grouping?: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  incrementality?: boolean;
  name?: string;
  toolTipText?: string;
  subHeader?: string[];
  yAxisId?: 'left' | 'right';
  dot?: ComponentProps<typeof Line>['dot'];
}

export type LineDataKeys = LineDataItem[];

interface LineChartProps {
  customTooltipHeader?: (val) => string;
  data: object[];
  dateGrouping?: string;
  colorMappings?: CustomColorMapping[];
  height?: number;
  groupedAreas?: any[];
  isAnimationActive?: boolean;
  leftYAxisDataKey?: string;
  legendTitle?: string | null;
  lineDataKeys: LineDataKeys;
  percentageMinTickGap?: number;
  percentDomain?: [number, number];
  customLeftYAxisDomain?: [number | string, number | string];
  customRightYAxisDomain?: [number | string, number | string];
  rightYAxisTickFormatter?: (value: any) => string;
  tooltipLabelFormatter?: (value: any) => string;
  tooltipFormatter?: (value: any) => string;
  tooltipShape?: 'circle' | 'square' | 'line';
  sortTooltipValues?: boolean;
  usePercentageYAxis?: boolean; // default to left side; use leftYAxisDataKey and leftYAxisLabel
  width?: number;
  xAxisDataKey: string;
  xAxisLabel?: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisTickFormatter?: (value: any) => string;
  useLeftYAxis?: boolean;
  usePrimaryColors?: boolean;
  maxChannelDimension?: string;
  enableChannelExpansion?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  onLegendClick?: (line: LineDataItem, index: number) => void;
}

const INACTIVE_COLOR = Neutral200;

const getTotalLineColor = (
  area: { name?: string; dataKey?: string } | null | undefined,
  colorMap: Record<string, string> | undefined
): string => {
  if (!area || !colorMap) {
    return INACTIVE_COLOR;
  }
  return colorMap[area.name || ''] || colorMap[area.dataKey || ''] || INACTIVE_COLOR;
};

interface LineChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: any[];
  customTooltipHeader?: (val: any) => string;
  dateGrouping?: string;
  fiscalCal: CompanyFiscalCal;
  tooltipLabelFormatter?: (value: any) => string;
  tooltipFormatter?: (value: any) => string;
  groupedAreas?: any[];
  sortTooltipValues?: boolean;
  tooltipShape?: 'circle' | 'square' | 'line';
}

const LineChartTooltip = ({
  active,
  label,
  payload,
  customTooltipHeader,
  dateGrouping,
  fiscalCal,
  tooltipLabelFormatter,
  tooltipFormatter,
  groupedAreas,
  sortTooltipValues,
  tooltipShape
}: LineChartTooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  const formattedHeaderLabel = customTooltipHeader
    ? customTooltipHeader(label)
    : headerFormatter(dateGrouping, String(label ?? ''), fiscalCal);

  let items = payload.map(item => ({
    label: tooltipLabelFormatter ? tooltipLabelFormatter(item.name) : (item.name as string),
    rawValue: item.value as number,
    value: tooltipFormatter ? tooltipFormatter(item.value) : (item.value as number),
    color: item.stroke,
    originalName: item.name as string
  }));

  if (groupedAreas && groupedAreas.length > 0) {
    items = items.map(item => {
      if (item.label === 'Total' && item.originalName && item.originalName.includes('__TOTAL__')) {
        const parts = item.originalName.split('__').filter(p => p !== '');
        if (parts.length > 0) {
          const channelName = parts[0];
          const otherItemsFromChannel = payload.filter((p: any) => {
            const pName = p.name as string;
            return pName !== item.originalName && pName.includes(channelName);
          });
          if (otherItemsFromChannel.length === 0) {
            const channelFormatted = tooltipLabelFormatter
              ? tooltipLabelFormatter(channelName)
              : channelName;
            return { ...item, label: channelFormatted };
          }
        }
      }
      return item;
    });
  }

  if (sortTooltipValues) {
    items = R.sortBy((item: any) => -item.rawValue, items);
  }
  const formattedItems = items.map(({ rawValue, originalName, ...item }) => item);

  return (
    <ChartTooltip headerLabel={formattedHeaderLabel} items={formattedItems} shape={tooltipShape} />
  );
};

export const LineChart: FC<LineChartProps> = ({
  customTooltipHeader,
  data,
  dateGrouping,
  colorMappings,
  groupedAreas,
  isAnimationActive = false,
  leftYAxisDataKey,
  legendTitle,
  lineDataKeys,
  percentageMinTickGap = 20,
  percentDomain = [0, 100],
  customLeftYAxisDomain = [0, 'auto'],
  customRightYAxisDomain = [0, 'auto'],
  rightYAxisTickFormatter,
  tooltipFormatter,
  tooltipLabelFormatter,
  tooltipShape,
  sortTooltipValues = true,
  useLeftYAxis,
  usePercentageYAxis = false,
  xAxisDataKey,
  xAxisLabel,
  xAxisTickFormatter,
  yAxisTickFormatter,
  usePrimaryColors = true,
  maxChannelDimension,
  enableChannelExpansion = false,
  showLegend = true,
  showTooltip = true,
  onLegendClick
}) => {
  const [activeLine, setActiveLine] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(
    enableChannelExpansion ? new Set() : new Set()
  );
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data
  });

  const xAxis = (
    <XAxis
      axisLine={false}
      dataKey={xAxisDataKey}
      // Reserve extra band height when a label is present so it clears the ticks.
      height={xAxisLabel ? xAxisHeight + X_AXIS_LABEL_HEIGHT : xAxisHeight}
      tick={TICK_STYLE}
      tickFormatter={xAxisTickFormatter}
      {...(xAxisLabel ? { label: renderXAxisLabel(xAxisLabel) } : {})}
    />
  );

  const hasDualAxis = useMemo(
    () => lineDataKeys.some(item => item.yAxisId === 'right'),
    [lineDataKeys]
  );

  const leftYAxis = (
    <YAxis
      axisLine={false}
      dataKey={leftYAxisDataKey}
      domain={customLeftYAxisDomain}
      orientation='left'
      tick={TICK_STYLE}
      tickFormatter={tick => `${yAxisTickFormatter ? yAxisTickFormatter(tick) : tick}`}
      tickLine={false}
      type='number'
      width={yAxisWidth}
      {...(hasDualAxis && { yAxisId: 'left' })}
    />
  );

  const rightYAxis = (
    <YAxis
      axisLine={false}
      domain={customRightYAxisDomain}
      orientation='right'
      tick={TICK_STYLE}
      tickFormatter={tick => `${rightYAxisTickFormatter ? rightYAxisTickFormatter(tick) : tick}`}
      tickLine={false}
      type='number'
      width={yAxisWidth}
      yAxisId='right'
    />
  );
  const percentageYAxis = (
    <YAxis
      axisLine={false}
      dataKey={leftYAxisDataKey}
      domain={percentDomain}
      minTickGap={percentageMinTickGap}
      orientation='left'
      tick={TICK_STYLE}
      tickLine={false}
      // recharts v3 paints no tick labels for this axis without an explicit tick array
      // (dataKey is undefined and the domain is fixed, so it can't derive them from data).
      // Matches the working StackedAreaChart percentageYAxis. All call sites use the default
      // percentDomain [0, 100].
      ticks={[0, 25, 50, 75, 100]}
      type='number'
      unit='%'
      width={yAxisWidth}
      // Mirror leftYAxis: only set an explicit yAxisId when there's a dual axis. For a
      // single-axis chart (e.g. Spend Decay), the <Line> series use recharts' default
      // yAxisId, so a hardcoded yAxisId='0' here mismatches under recharts v3 and the axis
      // renders no ticks. Omitting it lets both default to the same id.
      {...(hasDualAxis && { yAxisId: 'left' })}
    />
  );
  const tooltip = (
    <Tooltip
      content={
        <LineChartTooltip
          customTooltipHeader={customTooltipHeader}
          dateGrouping={dateGrouping}
          fiscalCal={fiscalCal}
          tooltipLabelFormatter={tooltipLabelFormatter}
          tooltipFormatter={tooltipFormatter}
          groupedAreas={groupedAreas}
          sortTooltipValues={sortTooltipValues}
          tooltipShape={tooltipShape}
        />
      }
      isAnimationActive={false}
      // pointerEvents:auto overrides recharts' default `none` on the wrapper so the cursor can
      // reach the tooltip and scroll its capped `.item-list` (see ChartTooltip.scss).
      wrapperStyle={{ zIndex: 20, pointerEvents: 'auto' }}
    />
  );

  const colorMap = useMemo(() => {
    if (R.isEmpty(lineDataKeys)) return undefined;
    return customColorMapping(lineDataKeys, colorMappings, usePrimaryColors);
  }, [colorMappings, lineDataKeys, usePrimaryColors]);

  const buildLines = useCallback(() => {
    let filteredLineDataKeys = lineDataKeys;
    if (
      enableChannelExpansion &&
      groupedAreas &&
      groupedAreas.length > 0 &&
      expandedChannels !== undefined
    ) {
      if (expandedChannels.size === 0) {
        filteredLineDataKeys = lineDataKeys.filter(dataKeyInfo => {
          const name = dataKeyInfo.name || dataKeyInfo.dataKey || '';
          const isTotalLine =
            name.includes('__TOTAL__') ||
            (dataKeyInfo.grouping && dataKeyInfo.grouping.platform === 'Total');

          if (!isTotalLine) {
            return groupedAreas.some(group =>
              group.areas?.some((area: any) => {
                const areaName = area.name || area.dataKey || '';
                const matchesLine = areaName === name || area.dataKey === dataKeyInfo.dataKey;
                if (matchesLine) {
                  return areaName.includes('__TOTAL__') || area.grouping?.platform === 'Total';
                }
                return false;
              })
            );
          }
          return isTotalLine;
        });
      } else {
        filteredLineDataKeys = lineDataKeys.filter(dataKeyInfo => {
          const { grouping } = dataKeyInfo;
          if (grouping && grouping.channel) {
            return expandedChannels.has(grouping.channel);
          }
          return groupedAreas.some(
            group =>
              expandedChannels.has(group.channel) &&
              group.areas?.some((area: any) => {
                const areaName = area.name || area.dataKey || '';
                const lineName = dataKeyInfo.name || dataKeyInfo.dataKey || '';
                return areaName === lineName || area.dataKey === dataKeyInfo.dataKey;
              })
          );
        });
      }
    }

    const lines = filteredLineDataKeys.map((dataKeyInfo, index: number) => {
      const isActiveLine = activeLine === null ? true : activeLine === dataKeyInfo.name;
      const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === dataKeyInfo.name;
      const activeColor = isActiveLine && colorMap ? colorMap[dataKeyInfo.name || ''] : GRAY_COLOR;
      const hoverColor =
        isHoveredLegend && colorMap ? colorMap[dataKeyInfo.name || ''] : GRAY_COLOR;
      if (!isActiveLine && activeLine !== null) {
        return null;
      }
      return (
        <Line
          dataKey={dataKeyInfo.dataKey}
          dot={dataKeyInfo.dot ?? false}
          isAnimationActive={isAnimationActive}
          key={index}
          stroke={activeLine ? activeColor : hoverColor}
          strokeWidth={3}
          type='monotone'
          {...(hasDualAxis && { yAxisId: dataKeyInfo.yAxisId || 'left' })}
        />
      );
    });
    return lines;
  }, [
    colorMap,
    isAnimationActive,
    enableChannelExpansion,
    lineDataKeys,
    activeLine,
    hoveredLegend,
    expandedChannels,
    groupedAreas,
    hasDualAxis
  ]);

  const useLeftAxis = (leftYAxisDataKey || useLeftYAxis) && !usePercentageYAxis;

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        if (group) {
          setActiveGroup(group.channel);
        }
      }
      onLegendClick?.(
        line,
        lineDataKeys.findIndex(item => item.name === line.name)
      );
    },
    [activeLine, lineDataKeys, onLegendClick]
  );

  const handleLegendEnter = useCallback(
    line => {
      if (hoveredLegend === line.name) {
        setHoveredLegend(null);
      } else {
        setHoveredLegend(line.name);
      }
    },
    [hoveredLegend]
  );

  const toggleChannelExpansion = useCallback((channel: string) => {
    setExpandedChannels(prev => {
      if (prev.has(channel)) {
        return new Set<string>();
      }
      return new Set<string>([channel]);
    });
  }, []);

  const buildLegend = useCallback(
    areas =>
      areas.map(area => {
        const isActiveArea = activeLine === null ? true : activeLine === area.name;
        const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
        const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
        const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
        return (
          <InteractiveDiv
            className='legend-item'
            key={area.name}
            onClick={() => handleLegendClick(area)}
            onMouseEnter={() => handleLegendEnter(area)}
            onMouseLeave={() => setHoveredLegend(null)}
          >
            <div className='legend-value'>
              <div className={isActiveArea ? '' : 'inactive-legend'}>
                <div>
                  <div className='non-group-legend-wrap'>
                    <div
                      className='line'
                      style={{
                        backgroundColor: `${activeLine ? activeColor : hoverColor}`
                      }}
                    />
                    <div className='legend-header'>{area.name}</div>
                    {area.toolTipText && (
                      <div className='tooltip-box'>
                        <InfoTooltip size={Size.SM}> {area.toolTipText} </InfoTooltip>
                      </div>
                    )}
                    {area.incrementality && (
                      <MdAutoGraph
                        className={`incrementality-icon ${
                          isActiveArea || activeLine === null ? 'active' : 'inactive'
                        }`}
                      />
                    )}
                  </div>
                  {area.subHeader && (
                    <div className='legend-sub-header'>
                      {area.subHeader.map(item => item.toLowerCase()).join(', ')}
                    </div>
                  )}
                </div>
                {area.grouping &&
                  (area.grouping.platform ||
                    area.grouping.tactic ||
                    area.grouping.brand_nonbrand) && (
                    <>
                      <div className='legend-header-wrapper'>
                        <div className='legend-header'>{area.grouping.channel}</div>
                        {area.incrementality && (
                          <MdAutoGraph
                            className={`incrementality-icon ${
                              isActiveArea || activeLine === null ? 'active' : 'inactive'
                            }`}
                          />
                        )}
                      </div>
                      <div className='sub-header-wrapper'>
                        <div
                          className='line'
                          style={{
                            backgroundColor: `${activeLine ? activeColor : hoverColor}`
                          }}
                        />
                        <div className='sub-header-1'>{area.grouping.platform}</div>
                        {area.grouping.platform &&
                          (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                            <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                          )}
                        <div className='sub-header-2'>{`${
                          area.grouping.tactic ? area.grouping.tactic : ''
                        }${area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''}${
                          area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''
                        }`}</div>
                      </div>
                    </>
                  )}
              </div>
            </div>
          </InteractiveDiv>
        );
      }),
    [activeLine, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  const buildLegendByGroupedArea = useCallback(
    () =>
      groupedAreas?.map((group, i) => {
        const totalArea =
          group.areas?.find(
            (area: any) =>
              area.name?.endsWith('__TOTAL__') ||
              area.name?.includes('__TOTAL__') ||
              area.grouping?.platform === 'Total'
          ) || group.areas?.[0];

        const isExpanded = enableChannelExpansion ? expandedChannels.has(group.channel) : true;
        const isChannelActive = activeGroup === group.channel || activeGroup === null;
        const hasAnyExpanded = enableChannelExpansion ? expandedChannels.size > 0 : false;
        const shouldGreyOut = enableChannelExpansion && hasAnyExpanded && !isExpanded;

        if (maxChannelDimension === 'Channel') {
          const channelArea = group.areas[0];
          const channelAreaName = channelArea.name;
          const isActiveArea = activeLine === null ? true : activeLine === channelAreaName;
          const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === channelAreaName;
          const activeColor = isActiveArea ? colorMap[channelAreaName] : INACTIVE_COLOR;
          const hoverColor = isHoveredLegend ? colorMap[channelAreaName] : INACTIVE_COLOR;

          return (
            <InteractiveDiv
              className='legend-item'
              key={`legenditemgroup${i}`}
              onClick={() => handleLegendClick(channelArea, group)}
              onMouseEnter={() => handleLegendEnter(channelArea)}
              onMouseLeave={() => setHoveredLegend(null)}
            >
              <div className='legend-value'>
                <div>
                  <div className='legend-header-wrapper channel-legend-header-wrapper'>
                    <div
                      className='channel-line'
                      style={{
                        backgroundColor: `${activeLine ? activeColor : hoverColor}`
                      }}
                    />
                    <div
                      className={`legend-header ${
                        activeGroup === group.channel || activeGroup === null
                          ? ''
                          : 'inactive-legend'
                      }`}
                    >
                      {group.channel}
                    </div>
                  </div>
                </div>
              </div>
            </InteractiveDiv>
          );
        }

        const totalLineColor = getTotalLineColor(totalArea, colorMap);

        const channelHeaderInner = (
          <div
            className={`legend-header ${isChannelActive && !shouldGreyOut ? '' : 'inactive-legend'}`}
          >
            <div className='legend-header-content'>
              {enableChannelExpansion && !isExpanded && totalArea && colorMap && (
                <div
                  className='line collapsed-line-indicator'
                  style={{
                    backgroundColor: totalLineColor
                  }}
                />
              )}
              <span>{group.channel}</span>
            </div>
            {enableChannelExpansion &&
              (isExpanded ? (
                <MdExpandLess className='expand-icon' />
              ) : (
                <MdExpandMore className='expand-icon' />
              ))}
          </div>
        );

        return (
          <div className='legend-item' key={`legenditemgroup${i}`}>
            <div className='legend-value'>
              {enableChannelExpansion ? (
                <InteractiveDiv
                  className='legend-header-wrapper clickable'
                  onClick={() => toggleChannelExpansion(group.channel)}
                >
                  {channelHeaderInner}
                </InteractiveDiv>
              ) : (
                <div className='legend-header-wrapper'>{channelHeaderInner}</div>
              )}
              {isExpanded && (
                <div className='expanded-content'>
                  {[...group.areas]
                    .sort((a, b) => {
                      const aIsTotal =
                        a.name?.includes('__TOTAL__') || a.grouping?.platform === 'Total';
                      const bIsTotal =
                        b.name?.includes('__TOTAL__') || b.grouping?.platform === 'Total';
                      if (aIsTotal && !bIsTotal) {
                        return -1;
                      }
                      if (!aIsTotal && bIsTotal) {
                        return 1;
                      }
                      return 0;
                    })
                    .map((area, j) => {
                      const isActiveArea = activeLine === null ? true : activeLine === area.name;
                      const isHoveredLegend =
                        hoveredLegend === null ? true : hoveredLegend === area.name;
                      const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                      const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                      return (
                        <InteractiveDiv
                          key={`legendlinechar${j}`}
                          className='sub-header-wrapper'
                          onClick={() => handleLegendClick(area, group)}
                          onMouseEnter={() => handleLegendEnter(area)}
                          onMouseLeave={() => setHoveredLegend(null)}
                        >
                          <div
                            className='line'
                            style={{
                              backgroundColor: `${activeLine ? activeColor : hoverColor}`
                            }}
                          />
                          <div className='sub-header-text-wrap'>
                            <div
                              className={`sub-header-1 ${isActiveArea ? '' : 'inactive-legend'}`}
                            >
                              {area.grouping?.platform || area.name}
                            </div>
                            {area.grouping?.platform &&
                              (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                                <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                              )}
                            {area.grouping && (
                              <div
                                className={`sub-header-2 ${isActiveArea ? '' : 'inactive-legend'}`}
                              >{`${area.grouping.tactic ? area.grouping.tactic : ''}${
                                area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''
                              }${
                                area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''
                              }`}</div>
                            )}
                            {area.incrementality && (
                              <MdAutoGraph
                                className={`incrementality-icon ${
                                  isActiveArea || activeLine === null ? 'active' : 'inactive'
                                }`}
                              />
                            )}
                          </div>
                        </InteractiveDiv>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        );
      }),
    [
      activeLine,
      hoveredLegend,
      colorMap,
      maxChannelDimension,
      activeGroup,
      handleLegendClick,
      handleLegendEnter,
      expandedChannels,
      toggleChannelExpansion,
      enableChannelExpansion,
      groupedAreas
    ]
  );

  return (
    <div className='line-wrapper'>
      <div className='line-chart' style={{ display: 'flex', flex: 1 }}>
        <div style={{ display: 'flex', flex: 1 }}>
          <AutoSizer
            renderProp={({ width, height }) => (
              <LC data={data} height={height ?? 0} width={width ?? 0} ref={setChartRef}>
                <CartesianGrid
                  stroke={CARTESIAN_GRID_STROKE}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  vertical={false}
                />
                {xAxis}
                {usePercentageYAxis && percentageYAxis}
                {useLeftAxis && leftYAxis}
                {hasDualAxis && rightYAxis}
                {showTooltip && tooltip}

                {buildLines()}
              </LC>
            )}
          />
        </div>
        {showLegend && (
          <div className='right-side-wrapper'>
            <div className='right-of-chart'>
              <div className='legend-title'>{legendTitle}</div>
              <div className='legend'>
                {(!groupedAreas || R.isEmpty(groupedAreas)) && buildLegend(lineDataKeys)}
                {groupedAreas && buildLegendByGroupedArea()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
